import React from "react";
import { useOnChangeWatcher } from "../../models/useOnChangeWatcher";
import { itx } from "../../translationItx/itx";
import ObjectReference from "../ObjectReference";
import Signal from "../../models/Signal";
import Device from "../../models/Device";
import EditableText from "../EditableText";
import { isUiModeProductAdmin } from "../../uiMode";
import Channel from "../../models/Channel";
import { moveSignalBackToIgnoreIfNoGroupAddresses } from "../ConnectSignal";

// Compact row for one manually connected signal (shown below the main signal list).

function ManualSignalRow(props: { signal: Signal; device: Device }) {
  const { signal } = props;
  useOnChangeWatcher(signal);

  const groupAddresses = signal.groupAddresses.items;

  return (
    <tr className="manual-signal-row">
      {/* Signal number */}
      <td
        className="manual-signal-row__number-cell"
        onClick={(ev) => {
          ev.stopPropagation();
          state.selectionManager.selectedObject = signal;
        }}
      >
        <span className="manual-signal-number">
          {itx("oneSignal.signalNumber", "Signal")} {signal.number}:
        </span>
      </td>

      {/* Group addresses */}
      <td className="manual-signal-row__addresses">
        {groupAddresses
          .filter((ga) => ga.isManualGroupAddress)
          .map((ga) => (
            <div
              key={ga.id}
              className="manual-signal-ga"
              onClick={(ev) => {
                ev.stopPropagation();
                state.selectionManager.selectedObject = ga;
              }}
            >
              <span className="manual-signal-ga__address">{ga.fullAddress}</span>
              {ga.name && <span className="manual-signal-ga__name">· {ga.name}</span>}
            </div>
          ))}
      </td>
    </tr>
  );
}

// Container for all group addresses – matches OneButton's ConnectionGroupElement outer wrapper.
function SignalGroupAddresses(props: { signal: Signal; device: Device }) {
  const { signal, device } = props;
  useOnChangeWatcher(signal);

  const groupAddresses = signal.groupAddresses.items;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        cursor: "default", // no itx
        minHeight: "100%",
        minWidth: "100%",
        alignItems: "center",
      }}
    >
      <i
        className="las la-bars"
        style={{
          backgroundColor: "##var(--lightLightGrey)",
          border: "1px solid var(--borderColorDarkGrey)",
          borderRadius: "10px 0px 0px 10px",
          height: "20px",
          width: "25px",
          padding: "2px",
          paddingLeft: "5px",
          color: groupAddresses.length > 0 ? "var(--defaultActiveColor)" : "var(--defaultNotActiveColor)", // no itx
        }}
        data-tooltip-id="tv-tooltip"
        data-tooltip-content={
          groupAddresses.length === 0
            ? state.translate("oneSignal.noGroupAddresses", "Keine Gruppenadresse vorhanden")
            : itx("oneSignal.groupAddresses", "Gruppenadressen")
        }
        data-tooltip-place="bottom"
      />
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          minHeight: "30px",
          minWidth: "50px",
          backgroundColor: "var(--lightLightGrey)",
          border: "1px solid var(--borderColorDarkGrey)",
          borderRadius: "0px 5px 5px 0px",
          padding: "5px",
          gap: "6px",
        }}
        data-tooltip-id="tv-tooltip"
        data-tooltip-content={
          groupAddresses.length === 0 ? state.translate("oneSignal.dropHint", "Keine Gruppenadresse verknüpft") : ""
        }
        data-tooltip-place="bottom"
      >
        {groupAddresses.length === 0 ? (
          <div style={{ fontSize: "11px", color: "var(--textColorLight)", fontStyle: "italic" }}>
            {itx("oneSignal.noGroupAddresses", "–")}
          </div>
        ) : (
          groupAddresses.map((ga) => (
            <ObjectReference
              key={ga.id}
              obj={ga}
              onUnlink={() => {
                signal.removeGroupAddress(ga, true);
                moveSignalBackToIgnoreIfNoGroupAddresses(device, signal);
              }}
            />
          ))
        )}
      </div>
    </div>
  );
}

export default function OneSignal(props: {
  button: Device;
  signal: Signal;
  index: number;
  redraw: () => void;
  withDelete: boolean;
}) {
  const { signal, button } = props;
  useOnChangeWatcher(signal);

  // Name element – pill shape matching OneButton's NameElement
  function NameElement() {
    const nameElementStyle: React.CSSProperties = {
      borderRadius: "30px",
      minWidth: "50px",
      height: "35px",
      alignItems: "center",
      position: "relative",
      display: "flex",
      flexDirection: "row",
    };

    const badgeStyle: React.CSSProperties = {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      borderTopLeftRadius: "30px", // no itx
      borderBottomLeftRadius: "30px", // no itx
      backgroundColor: "lightgray", // no itx
      minWidth: "60px",
      paddingLeft: "10px",
      paddingRight: "10px",
      height: "100%",
      cursor: "default",
      fontSize: "12px",
      whiteSpace: "nowrap",
    };

    return (
      <td
        colSpan={2}
        style={{
          paddingLeft: "5px",
          paddingRight: "5px",
          verticalAlign: "middle",
          minWidth: "200px",
        }}
        onClick={(ev) => {
          if (state.selectionManager.selectedObject !== signal) {
            state.selectionManager.selectedObject = signal;
          }
        }}
      >
        <div style={nameElementStyle}>
          <div className="elm1" style={badgeStyle}>
            {signal.number}
          </div>
          <EditableText
            obj={signal}
            property="name" // no itx
            propertyType="text"
            multiline={false}
            disableEdit={true}
            style={{
              flexGrow: "1",
              height: "100%",
              paddingLeft: "10px",
              paddingRight: "10px",
              marginLeft: "0px",
              marginRight: "0px",
            }}
          />
          <EditableText
            obj={signal}
            property="knxFunctionText" // no itx
            propertyType="text"
            multiline={false}
            disableEdit={true}
            style={{
              flexGrow: "1",
              height: "100%",
              paddingLeft: "10px",
              paddingRight: "10px",
              marginLeft: "0px",
              marginRight: "0px",
            }}
          />
        </div>
      </td>
    );
  }

  return (
    <tr
      key={signal.id}
      style={{ alignItems: "center", verticalAlign: "middle" }}
      onClick={(ev) => {
        if (state.selectionManager.selectedObject !== signal) {
          state.selectionManager.selectedObject = signal;
        }
      }}
    >
      {/* Delete / index column */}
      <td style={{ verticalAlign: "top" }}>
        <div style={{ position: "relative", display: "flex", height: "100%" }}>&nbsp;</div>
      </td>
      {/* Signal number tag */}
      <td
        style={{ paddingLeft: "0px", paddingRight: "5px" }}
        onClick={(ev) => {
          if (state.selectionManager.selectedObject !== signal) {
            state.selectionManager.selectedObject = signal;
          }
        }}
      >
        {isUiModeProductAdmin() ? signal.number : ""}
      </td>
      {/* Placeholder (matches split-button column in OneButton) */}
      <td />
      {/* Float-right placeholder */}
      <td style={{ float: "right" }} />
      {/* Name pill */}
      <NameElement />
      {/* Signal type
      <td
        style={{ paddingLeft: "5px", verticalAlign: "middle" }}
        onClick={(ev) => {
          if (state.selectionManager.selectedObject !== signal) {
            state.selectionManager.selectedObject = signal;
          }
        }}
      >
        {itx("SignalTypes." + signal.type.name)}
      </td> */}
      {/* Group addresses and connections */}
      <td
        style={{ verticalAlign: "middle" }}
        onClick={(ev) => {
          if (state.selectionManager.selectedObject !== signal) {
            state.selectionManager.selectedObject = signal;
          }
        }}
      >
        <SignalGroupAddresses signal={signal} device={button} />
      </td>
    </tr>
  );
}

export function OneButtonManualSignals(props: { button: Device; buttonChannel: Channel; redraw: () => void }) {
  const { button, buttonChannel } = props;
  const manualSignals = buttonChannel.signals
    .asArray()
    .filter((signal) => signal.groupAddresses.asArray().some((groupAddress) => groupAddress.isManualGroupAddress));

  if (manualSignals.length === 0) return null;

  return (
    <tr className="manual-signals-section">
      <td colSpan={20} className="manual-signals-section__cell">
        <div className="manual-signals-section__header">
          <span className="manual-signals-section__label">{itx("oneSignal.manualSignals", "Manuelle Signale")} :</span>
          <table className="manual-signals-section__table">
            <tbody>
              {manualSignals.map((signal: Signal) => (
                <ManualSignalRow key={signal.id} signal={signal} device={button} />
              ))}
            </tbody>
          </table>
        </div>
      </td>
    </tr>
  );
}
